import LazySection from '@/components/Common/LazySection';
import LogoMarquee, {CARD_HEIGHT, CARD_HEIGHT_COMPACT, LogoMarqueeItem} from '@/components/Common/LogoMarquee';
import {NARROW_LAYOUT_MAX, maxWidth} from '@/styles/breakpoints';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, {memo, useMemo} from 'react';
import {sectionHeadingSx} from '@/styles/sectionHeading';

// Two rows travelling in opposite directions, in pixels per second. The speeds are
// deliberately unequal: matched speeds make the two rows read as one rigid block
// sliding apart, while a slight offset keeps the counter-motion legible.
const ROW_SPEEDS = [40, 35] as const;

// No gap between the rows: each LogoMarquee already carries 16px of vertical padding
// so its cards' shadows are not clipped, and that padding supplies the row separation.
// Adding a gap on top of it would stack into an oversized void between the two rows.
const rowsWrapperSx = {display: 'flex', flexDirection: 'column'};
// Flush rows give up that padding, so the rows need a gap of their own to stay apart.
const FLUSH_ROW_GAP_PX = 16;
const flushRowsWrapperSx = {...rowsWrapperSx, gap: `${FLUSH_ROW_GAP_PX}px`};

// Reserves the marquee's height while LazySection holds the logos back, so mounting
// them does not shift the sections below (CLS). Tracks LogoMarquee's own sizing: two
// rows of cards, each row padded 16px top and bottom for its shadow. The cards are
// compact on phones and below 1500px, so the reservation steps down with them — a
// wrapper rather than LazySection's own `minHeight`, which takes a single number.
const ROW_PADDING_PX = 32;
const rowsHeight = (cardHeight: number) => 2 * (cardHeight + ROW_PADDING_PX);
const marqueeReserveSx = {
    minHeight: {xs: rowsHeight(CARD_HEIGHT_COMPACT), md: rowsHeight(CARD_HEIGHT)},
    [maxWidth(NARROW_LAYOUT_MAX)]: {minHeight: rowsHeight(CARD_HEIGHT_COMPACT)}
} as const;
const flushRowsHeight = (cardHeight: number) => 2 * cardHeight + FLUSH_ROW_GAP_PX;
const flushMarqueeReserveSx = {
    minHeight: {xs: flushRowsHeight(CARD_HEIGHT_COMPACT), md: flushRowsHeight(CARD_HEIGHT)},
    [maxWidth(NARROW_LAYOUT_MAX)]: {minHeight: flushRowsHeight(CARD_HEIGHT_COMPACT)}
} as const;

const ClientServed = (props: any) => {
    // `flush` drops the rows' 16px top/bottom padding from the layout (see LogoMarquee).
    const {heading, subheading, contentData, flush = false} = props;

    // Split down the middle, first row taking the extra logo on an odd count.
    const [topRow, bottomRow] = useMemo(() => {
        const items: LogoMarqueeItem[] = contentData ?? [];
        const half = Math.ceil(items.length / 2);
        return [items.slice(0, half), items.slice(half)];
    }, [contentData]);

    return (
        <Box>
            {/* Skipped with no heading, rather than leaving an empty <h2> and its spacing. */}
            {heading && (
                <MUIContainer>
                    <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={{xs: 2, md: 2.5}}>
                        <Typography variant="h1" component="h2" mb={1.5} className="font-headers" sx={sectionHeadingSx}>
                            {heading}
                        </Typography>
                        {/* The home page passes no subheading — rendering the element regardless
                        left an empty paragraph holding a line's worth of space under the heading. */}
                        {subheading && (
                            <Typography variant="subtitle1" component="p" className="font-regular-text">
                                {subheading}
                            </Typography>
                        )}
                    </Box>
                </MUIContainer>
            )}
            {/* Deliberately outside MUIContainer: the rows must run edge to edge so logos
                scroll in from off-screen rather than popping in at the container's margin. */}
            <Box sx={flush ? flushMarqueeReserveSx : marqueeReserveSx}>
                <LazySection>
                    <Box sx={flush ? flushRowsWrapperSx : rowsWrapperSx}>
                        <LogoMarquee items={topRow} direction="left" speed={ROW_SPEEDS[0]} flush={flush} />
                        <LogoMarquee items={bottomRow} direction="right" speed={ROW_SPEEDS[1]} flush={flush} />
                    </Box>
                </LazySection>
            </Box>
        </Box>
    );
};

export default memo(ClientServed);
